/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: field-validation-component.ts
 *
 * Created by kyang on 2020-09-14.
 */

//
// FieldValidation
//
// Given:
//  * a list of N objects that look something like this:
//      {
//          getDescription: function() { ... },
//          isValid: function() { ... },
//          isWarning: function() { ... },
//      }
//
// This component displays a list of descriptions in a bulleted list.
// Bullet changes based on the output of isValid() & isWarning():
//     - If isValid() returns true, bullet is a checkmark.
//     - Otherwise, if isWarning() returns true, bullet is a warning triangle.
//     - Otherwise, bullet is an X.
//
// getDescription() is a function to allow dynamic descriptions, such as:
//     - Current length: # / 15
//
// See the bindings for the complete list of configurable options.
//
// The general concept is to indicate why a field is invalid.

function FieldValidationController (
    this: JS5Object,
    _: JS5Object,
    $animate: JS5Object,
    $element: JS5Object,
) {
    const INVALID_STATUS = 1;
    const WARNING_STATUS = 2;
    const VALID_STATUS = 3;

    //================//
    // Template Logic //
    //================//

    this.isValid = (requirement: JS5Object) => {
        return requirement.status === VALID_STATUS;
    }

    this.isWarning = (requirement: JS5Object) => {
        return requirement.status === WARNING_STATUS;
    }

    this.isFail = (requirement: JS5Object) => {
        return requirement.status === INVALID_STATUS;
    }

    this.getDescription = (requirement: JS5Object) => {
        return requirement.getDescription();
    }


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$doCheck = () => {
        let someRequirementsInvalid = false;
        _.forEach(this.requirements, (requirement: JS5Object) => {
            if (requirement.isValid()) {
                requirement.status = VALID_STATUS;
            } else if (_.invoke(requirement, 'isWarning')) {
                requirement.status = WARNING_STATUS;
                someRequirementsInvalid = someRequirementsInvalid || !!this.invalidOnWarn;
            } else {
                requirement.status = INVALID_STATUS;
                someRequirementsInvalid = true;
            }
        });
        if (this.editorForm) {
            this.editorForm.$setValidity('requirements', !someRequirementsInvalid);
        }
    };

    this.$onInit = () => {
        // Workaround a bug with ng-class:
        // If class is added during a $digest cycle,
        // and next frame, the next $digest cycle tries to remove that class...
        // The class will not be removed.
        $animate.enabled($element[0], false);
    }
}

angular.module('prism-ui.components').component('fieldValidation', {
    templateUrl: 'components/visual/fieldValidation/field-validation-template.html',
    controller: FieldValidationController,
    bindings: {
        /*
         List Properties
         header:       (string; required) Text to display at the top of this list
         requirements: (JS5Object[]; required) Array of objects. Each object can
                       declare 3 functions:
                           - getDescription(): (return string; required) Current text
                                               to display for this list item
                           - isValid():   (return boolean; required) If returns true,
                                          uses a checkmark as this item's bullet
                           - isWarning(): (return boolean; optional) If not valid,
                                          and this function returns true, uses a
                                          warning triangle as this item's bullet
         invalidOnWarn: (boolean; optional) If true, warnings will invalidate this
                        component's form
         */
        header: '@',
        requirements: '<',
        invalidOnWarn: '<',
    }
});
